import { DEFAULT_COUNTRY, formatNationalPhone, getCountryByPrefix, getCountryPrefix, getPhoneCountries, normalizePrefix, onlyDigits, parseInternational, PhoneCountry, PhoneValue } from 'utils/phone'
import { autoUpdate, flip, hide, offset, shift, size, useFloating } from '@floating-ui/react-dom'
import React, { forwardRef, useEffect, useMemo, useRef, useState } from 'react'
import * as Flags from 'country-flag-icons/react/3x2'
import useTranslation from 'hooks/useTranslation'
import { CountryCode } from 'libphonenumber-js'
import { getPortalRoot } from 'utils/portal'
import { createPortal } from 'react-dom'
import './style.scss'

type FocusAction = (_: React.FocusEvent<HTMLInputElement, Element>) => void

interface PhoneInputProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'value' | 'prefix' | 'onChange'> {
  name?: string
  /** numer krajowy (bez prefiksu), np. "531876282" */
  value: string
  /** numer kierunkowy, np. "+48" */
  prefix?: string
  required?: boolean
  disabled?: boolean
  className?: string
  warning?: string | boolean
  label?: string
  setValue: (value: PhoneValue) => void
  onNonPhoneInput?: (raw: string) => void
  enterClicked?: (e: HTMLInputElement) => void
  blurHandler?: FocusAction
  focusHandler?: FocusAction
}

const MAX_DIGITS = 15
/** Znaki, których nie ma w numerze telefonu, a są w adresie e-mail */
const NON_PHONE_CHARS = /[\p{L}@._]/u
/**
 * Ile czekać na kolejne znaki, zanim zgłosimy onNonPhoneInput.
 * Safari (desktop i iOS) wstawia login z podpowiedzi znak po znaku — gdyby rodzic
 * podmienił pole już po pierwszym znaku, reszta trafiłaby do odmontowanego elementu.
 */
const NON_PHONE_DEBOUNCE_MS = 150
const MAX_DROPDOWN_HEIGHT = 288
const POPUP_BACKDROP = '.popup__bg'

const swallowNextClick = () => {
  const stop = (e: MouseEvent) => {
    e.stopPropagation()
    e.preventDefault()
    cleanup()
  }
  const cleanup = () => {
    document.removeEventListener('click', stop, true)
    clearTimeout(timeout)
  }
  const timeout = setTimeout(cleanup, 1000)
  document.addEventListener('click', stop, true)
}

const Flag: React.FC<{ iso: CountryCode }> = ({ iso }) => {
  const Component = (Flags as Record<string, React.ComponentType<React.SVGProps<SVGSVGElement>>>)[iso]
  return Component
    ? <Component className='phoneInput__flag' aria-hidden />
    : <span className='phoneInput__flag --empty' aria-hidden />
}

const PhoneInput = forwardRef<HTMLInputElement, PhoneInputProps>(({
  name = 'phone',
  value,
  prefix,
  required,
  disabled = false,
  className = '',
  warning,
  label,
  setValue,
  onNonPhoneInput,
  enterClicked,
  blurHandler,
  focusHandler,
  ...restAttr
}, ref) => {
  const { t, lang } = useTranslation('basic.phoneInput')
  const countries = useMemo(() => getPhoneCountries(lang), [lang])

  const [country, setCountry] = useState<CountryCode>(() => getCountryByPrefix(prefix) || DEFAULT_COUNTRY)
  const [focused, setFocus] = useState(false)
  const [expanded, expand] = useState(false)
  const [search, setSearch] = useState('')
  const [highlighted, setHighlighted] = useState(0)
  /** Tekst niebędący numerem (np. początek e-maila) — pokazywany bez formatowania, dopóki rodzic nie przełączy pola */
  const [nonPhoneRaw, setNonPhoneRaw] = useState<string | null>(null)
  const nonPhoneRawRef = useRef<string | null>(null)
  const nonPhoneTimer = useRef<ReturnType<typeof setTimeout>>()
  const onNonPhoneInputRef = useRef(onNonPhoneInput)
  onNonPhoneInputRef.current = onNonPhoneInput

  useEffect(() => () => clearTimeout(nonPhoneTimer.current), [])

  const updateNonPhoneRaw = (raw: string | null) => {
    nonPhoneRawRef.current = raw
    setNonPhoneRaw(raw)
    clearTimeout(nonPhoneTimer.current)
    if (raw === null) return
    nonPhoneTimer.current = setTimeout(() => {
      const latest = nonPhoneRawRef.current
      if (latest !== null) onNonPhoneInputRef.current?.(latest)
    }, NON_PHONE_DEBOUNCE_MS)
  }

  const searchRef = useRef<HTMLInputElement>(null)
  const listRef = useRef<HTMLUListElement>(null)
  const inputRef = useRef<HTMLInputElement | null>(null)
  const countryButtonRef = useRef<HTMLButtonElement>(null)

  const { refs, floatingStyles, middlewareData } = useFloating<HTMLDivElement>({
    open: expanded,
    placement: 'bottom-start',
    strategy: 'fixed',
    whileElementsMounted: autoUpdate,
    middleware: [
      // nad polem większy odstęp — etykieta pola wystaje ponad jego ramkę
      offset(({ placement }) => placement.startsWith('top') ? 12 : 4),
      flip({ padding: 8 }),
      shift({ padding: 8 }),
      size({
        padding: 8,
        apply({ availableHeight, rects, elements }) {
          Object.assign(elements.floating.style, {
            width: `${rects.reference.width}px`,
            maxHeight: `${Math.max(120, Math.min(availableHeight, MAX_DROPDOWN_HEIGHT))}px`,
          })
        },
      }),
      hide({ strategy: 'referenceHidden' }),
    ],
  })

  // Escape obsługujemy sami (onSearchKeyDown), żeby nie zamykał przy okazji modala.
  // Klik w tło modala przy otwartej liście zamyka tylko listę — połykamy ten jeden click,
  // inaczej Popup uznałby go za zamknięcie całego modala.
  useEffect(() => {
    if (!expanded) return
    const onOutsidePress = (event: Event) => {
      const target = event.target as Element | null
      if (!target) return
      if (refs.reference.current instanceof Element && refs.reference.current.contains(target)) return
      if (refs.floating.current?.contains(target)) return
      if (target.matches?.(POPUP_BACKDROP)) swallowNextClick()
      expand(false)
    }
    document.addEventListener('mousedown', onOutsidePress, true)
    document.addEventListener('touchstart', onOutsidePress, true)
    return () => {
      document.removeEventListener('mousedown', onOutsidePress, true)
      document.removeEventListener('touchstart', onOutsidePress, true)
    }
  }, [expanded, refs])

  const currentPrefix = normalizePrefix(prefix) || getCountryPrefix(country)

  // prefiks zmieniony z zewnątrz (np. dane z API) -> dopasuj kraj
  useEffect(() => {
    const matched = getCountryByPrefix(prefix, country)
    if (matched && matched !== country) setCountry(matched)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prefix])

  // brak prefiksu w formularzu -> ustaw domyślny
  useEffect(() => {
    if (!normalizePrefix(prefix)) setValue({ prefix: getCountryPrefix(country), phone: onlyDigits(value) })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (!expanded) {
      setSearch('')
      return
    }
    setHighlighted(Math.max(0, countries.findIndex(c => c.iso === country)))
    const timeout = setTimeout(() => searchRef.current?.focus({ preventScroll: true }))
    return () => clearTimeout(timeout)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [expanded])

  const filtered = useMemo(() => {
    const query = search.trim().toLowerCase()
    if (!query) return countries
    const digits = onlyDigits(query)
    return countries.filter(c =>
      c.name.toLowerCase().includes(query) ||
      c.iso.toLowerCase() === query ||
      (digits && c.prefix.slice(1).startsWith(digits))
    )
  }, [countries, search])

  useEffect(() => {
    setHighlighted(0)
  }, [search])

  useEffect(() => {
    const item = listRef.current?.children[highlighted] as HTMLElement | undefined
    item?.scrollIntoView({ block: 'nearest' })
  }, [highlighted])

  const selectCountry = (c: PhoneCountry) => {
    setCountry(c.iso)
    setValue({ prefix: c.prefix, phone: onlyDigits(value) })
    expand(false)
    setTimeout(() => inputRef.current?.focus())
  }

  const onPhoneChange: React.ChangeEventHandler<HTMLInputElement> = e => {
    const raw = e.target.value
    if (nonPhoneRawRef.current !== null) {
      // pole pokazuje już tekst „nie-numer” — zbieramy dalsze znaki bez formatowania
      const compact = raw.replace(/\s/g, '')
      if (NON_PHONE_CHARS.test(compact)) {
        updateNonPhoneRaw(compact)
        return
      }
      // skasowano litery -> z powrotem zwykły numer
      updateNonPhoneRaw(null)
    }
    const international = parseInternational(raw)
    if (international) {
      const matched = getCountryByPrefix(international.prefix, country)
      if (matched) setCountry(matched)
      setValue(international)
      return
    }
    if (raw && !/^[\d\s()-]*$/.test(raw)) {
      if (onNonPhoneInput && NON_PHONE_CHARS.test(raw)) {
        // dopisano znak na końcu sformatowanego numeru -> zachowaj same cyfry wpisane wcześniej
        const formatted = formatNationalPhone(value, country)
        const typed = formatted && raw.startsWith(formatted)
          ? onlyDigits(value) + raw.slice(formatted.length)
          : raw
        updateNonPhoneRaw(typed.replace(/\s/g, ''))
      }
      return
    }
    let digits = onlyDigits(raw)
    // usunięto sam znak formatowania (spacja, nawias) -> usuń też cyfrę przed nim
    if (raw.length < formatNationalPhone(value, country).length && digits === onlyDigits(value)) digits = digits.slice(0, -1)
    digits = digits.slice(0, MAX_DIGITS - onlyDigits(currentPrefix).length)
    setValue({ prefix: currentPrefix, phone: digits })
  }

  const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && nonPhoneRawRef.current !== null) {
      // Enter w trakcie zbierania tekstu (np. Safari kończy autouzupełnianie) — przełącz od razu,
      // a wysłanie formularza zostaw rodzicowi (Login/Form czeka, aż login przestanie się zmieniać)
      clearTimeout(nonPhoneTimer.current)
      const raw = nonPhoneRawRef.current
      nonPhoneRawRef.current = null
      onNonPhoneInputRef.current?.(raw)
    }
    if (e.key === 'Enter' && enterClicked) {
      const target = e.target as HTMLInputElement
      target.blur()
      enterClicked(target)
    }
  }

  const onSearchKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault()
        setHighlighted(i => Math.min(i + 1, filtered.length - 1))
        break
      case 'ArrowUp':
        e.preventDefault()
        setHighlighted(i => Math.max(i - 1, 0))
        break
      case 'Enter':
        e.preventDefault()
        if (filtered[highlighted]) selectCountry(filtered[highlighted])
        break
      case 'Escape':
        // stopPropagation: Popup nasłuchuje Escape na document i zamknąłby cały modal
        e.stopPropagation()
        expand(false)
        countryButtonRef.current?.focus()
        break
      case 'Tab':
        expand(false)
        break
    }
  }

  return (
    <div
      ref={refs.setReference}
      className={`input__default phoneInput${focused || expanded ? ' --focused' : ''}${disabled ? ' --disabled' : warning ? ' --warning' : ''}${expanded ? ' --expanded' : ''} ${className}`}>
      {label && <label className='input__label' htmlFor={name}>
        <span className='label--warning'>{required ? '* ' : ''}</span>
        <span>{label} </span>
        {warning && typeof warning === 'string' && <span className='label--warning'>
          - {warning}
        </span>}
      </label>}

      <div className='input__main phoneInput__main'>
        <button
          ref={countryButtonRef}
          type='button'
          className='phoneInput__country'
          disabled={disabled}
          aria-haspopup='listbox'
          aria-expanded={expanded}
          aria-label={t('countryCode')}
          onClick={() => expand(!expanded)}>
          <Flag iso={country} />
          <span className='phoneInput__prefix'>{currentPrefix}</span>
          <svg className='phoneInput__chevron' width='10' height='6' viewBox='0 0 10 6' aria-hidden>
            <path d='M1 1l4 4 4-4' fill='none' stroke='currentColor' strokeWidth='1.5' strokeLinecap='round' strokeLinejoin='round' />
          </svg>
        </button>

        <span className='phoneInput__divider' />

        <input
          ref={r => {
            inputRef.current = r
            if (typeof ref === 'function') ref(r)
            else if (ref) ref.current = r
          }}
          className='input phoneInput__input'
          id={name}
          name={name}
          type='tel'
          inputMode='tel'
          autoComplete='tel-national'
          value={nonPhoneRaw ?? formatNationalPhone(value, country)}
          disabled={disabled}
          {...restAttr}
          onKeyDown={onKeyDown}
          onChange={onPhoneChange}
          onFocus={e => {
            setFocus(true)
            focusHandler && focusHandler(e)
          }}
          onBlur={e => {
            setFocus(false)
            blurHandler && blurHandler(e)
          }}
        />
      </div>

      {expanded && createPortal(
        <div
          ref={refs.setFloating}
          className='phoneInput__dropdown'
          style={{
            ...floatingStyles,
            visibility: middlewareData.hide?.referenceHidden ? 'hidden' : undefined,
          }}
          // Zdarzenia z portalu bąbelkują drzewem Reacta do Popup: klik byłby
          // uznany za „klik poza modalem”, a przeciągnięcie palcem za gest zamknięcia.
          onClick={e => e.stopPropagation()}
          onTouchStart={e => e.stopPropagation()}>
          <input
            ref={searchRef}
            className='phoneInput__search'
            value={search}
            placeholder={t('searchCountry')}
            onChange={e => setSearch(e.target.value)}
            onKeyDown={onSearchKeyDown}
          />
          <ul ref={listRef} className='phoneInput__list' role='listbox'>
            {filtered.length === 0 && <li className='phoneInput__empty'>{t('noCountriesFound')}</li>}
            {filtered.map((c, i) => <li
              key={c.iso}
              role='option'
              aria-selected={c.iso === country}
              className={`phoneInput__option${c.iso === country ? ' --selected' : ''}${i === highlighted ? ' --highlighted' : ''}`}
              onMouseEnter={() => setHighlighted(i)}
              onClick={() => selectCountry(c)}>
              <Flag iso={c.iso} />
              <span className='phoneInput__option__name'>{c.name}</span>
              <span className='phoneInput__option__prefix'>{c.prefix}</span>
            </li>)}
          </ul>
        </div>,
        getPortalRoot()
      )}
    </div>
  )
})

export default PhoneInput
